import { Paperclip, FileOutput, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import type {
  SessionArtifactRecord,
  SessionFileRecord,
} from "../../../../../packages/shared-types/src/index.js";

export function ConversationAssetsPanel(params: {
  files: SessionFileRecord[];
  artifacts: SessionArtifactRecord[];
  uploading: boolean;
  onUploadClick: () => void;
  onDownloadFile: (fileId: string) => void;
  onDownloadArtifact: (artifactId: string) => void;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-md border p-4" style={{ borderColor: "var(--border)" }}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Paperclip size={14} />
            <h3 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
              Session files
            </h3>
          </div>
          <Button size="sm" variant="outline" onClick={params.onUploadClick} disabled={params.uploading}>
            <Upload size={14} />
            {params.uploading ? "Uploading..." : "Attach files"}
          </Button>
        </div>
        <AssetList
          emptyLabel="No attached files yet."
          items={params.files.map((file) => ({
            id: file.fileId,
            title: file.filename,
            subtitle: `${file.mimeType} · ${formatBytes(file.sizeBytes)}`,
            onDownload: () => params.onDownloadFile(file.fileId),
          }))}
        />
      </section>

      <section className="rounded-md border p-4" style={{ borderColor: "var(--border)" }}>
        <div className="mb-3 flex items-center gap-2">
          <FileOutput size={14} />
          <h3 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
            Generated outputs
          </h3>
        </div>
        <AssetList
          emptyLabel="No generated outputs yet."
          items={params.artifacts.map((artifact) => ({
            id: artifact.artifactId,
            title: artifact.filename,
            subtitle: `${artifact.mimeType} · ${formatBytes(artifact.sizeBytes)}`,
            onDownload: () => params.onDownloadArtifact(artifact.artifactId),
          }))}
        />
      </section>
    </div>
  );
}

function AssetList(params: {
  emptyLabel: string;
  items: Array<{
    id: string;
    title: string;
    subtitle: string;
    onDownload: () => void;
  }>;
}) {
  if (!params.items.length) {
    return (
      <div className="rounded-md border border-dashed px-3 py-6 text-sm" style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}>
        {params.emptyLabel}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {params.items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={item.onDownload}
          className="flex w-full items-center justify-between rounded-md border px-3 py-3 text-left transition-colors hover:bg-[var(--bg-hover)]"
          style={{ borderColor: "var(--border)" }}
        >
          <div className="min-w-0">
            <div className="truncate text-sm font-medium" style={{ color: "var(--text-strong)" }}>
              {item.title}
            </div>
            <div className="truncate text-xs" style={{ color: "var(--muted-foreground)" }}>
              {item.subtitle}
            </div>
          </div>
          <span className="text-xs underline" style={{ color: "var(--accent)" }}>
            Download
          </span>
        </button>
      ))}
    </div>
  );
}

function formatBytes(value: number): string {
  if (value < 1024) {
    return `${value} B`;
  }
  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
